/* ============================================================
   screen-upload.jsx — Upload page (login-gated)
   ============================================================ */

function parseUploadTags(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function Upload({ auth, onLogin, go, toast }) {
  if (!auth) return <UploadGate onLogin={onLogin} go={go} />;
  return <UploadStudio auth={auth} go={go} toast={toast} />;
}

function UploadStudio({ auth, go, toast }) {
  const { lang, t } = useT();
  const [prompt, setPrompt] = React.useState("");
  const [tagsInput, setTagsInput] = React.useState("");
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [fileMeta, setFileMeta] = React.useState(null);
  const [recentItems, setRecentItems] = React.useState([]);
  const fileInputRef = React.useRef(null);
  const display = displayUserName(auth);
  const tags = React.useMemo(() => parseUploadTags(tagsInput), [tagsInput]);
  const canSubmit = Boolean(previewUrl && prompt.trim() && tags.length);

  const onPickImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    setPreviewUrl(dataUrl);
    setFileMeta({ name: file.name, size: file.size, type: file.type || "image/*" });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      author: display,
      handle: auth.handle || "me",
      prompt: prompt.trim(),
      tags,
      imageUrl: previewUrl,
      fileName: fileMeta?.name || "image",
    };
    setRecentItems((items) => [entry, ...items].slice(0, 4));
    setPrompt("");
    setTagsInput("");
    setPreviewUrl("");
    setFileMeta(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast?.(t("up_success"));
  };

  return (
    <div className="container" style={{ padding: "32px 24px 72px", display: "grid", gap: 24 }}>
      <div style={{ display: "grid", gap: 8 }}>
        <div className="eyebrow">Upload</div>
        <h1 style={{ fontSize: "clamp(28px, 3vw, 40px)" }}>{t("up_title")}</h1>
        <p className="muted" style={{ fontSize: 15, maxWidth: 720 }}>{t("up_sub")}</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(320px, .9fr)", gap: 24, alignItems: "start" }} className="upload-grid">
        <form className="card" onSubmit={onSubmit} style={{ padding: 22, borderRadius: "var(--r-xl)", display: "grid", gap: 20 }}>
          <UploadField label={t("up_name")}>
            <div style={uploadReadOnlyStyle}>
              <Avatar author={auth} size={36} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{display}</div>
                <div className="muted" style={{ fontSize: 13 }}>@{auth.handle || "me"}</div>
              </div>
            </div>
          </UploadField>

          <UploadField label={t("up_image")}>
            <div style={{ display: "grid", gap: 12 }}>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickImage} style={{ display: "none" }} />
              <button type="button" className="btn btn-line" onClick={() => fileInputRef.current?.click()} style={{ width: "100%", justifyContent: "center", height: 48, borderStyle: "dashed" }}>
                <Icon name="image" size={18} stroke={2} /> {previewUrl ? t("up_change") : t("up_pick")}
              </button>
              <div className="muted" style={{ fontSize: 13 }}>{fileMeta?.name || t("up_image_hint")}</div>
            </div>
          </UploadField>

          <UploadField label={t("up_prompt")}>
            <textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder={t("up_prompt_ph")}
              rows={7}
              style={uploadTextareaStyle}
            />
          </UploadField>

          <UploadField label={t("up_tags")}>
            <div style={{ display: "grid", gap: 10 }}>
              <input
                value={tagsInput}
                onChange={(event) => setTagsInput(event.target.value)}
                placeholder={t("up_tags_ph")}
                style={uploadInputStyle}
              />
              <div className="muted" style={{ fontSize: 13 }}>{t("up_tags_hint")}</div>
              {tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {tags.map((tag) => (
                    <span key={tag} className="chip" style={{ height: 32 }}>{lang === "en" ? (TAG_EN[tag] || tag) : tag}</span>
                  ))}
                </div>
              )}
            </div>
          </UploadField>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-spark btn-lg" type="submit" disabled={!canSubmit} style={{ opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? "pointer" : "not-allowed" }}>
              <Icon name="image" size={18} stroke={2} /> {t("up_submit")}
            </button>
            <button className="btn btn-ghost btn-lg" type="button" onClick={() => go("gallery")}>
              {t("back_gallery")}
            </button>
          </div>
        </form>

        <div style={{ display: "grid", gap: 16 }}>
          <section className="card" style={{ padding: 20, borderRadius: "var(--r-xl)", display: "grid", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <h2 style={{ fontSize: 18 }}>{t("up_preview")}</h2>
              <span className="muted" style={{ fontSize: 13 }}>{canSubmit ? t("up_submit") : t("up_submit_idle")}</span>
            </div>
            <div style={{ borderRadius: "var(--r-lg)", overflow: "hidden", background: "var(--surface-2)", border: "1px solid var(--line-2)", aspectRatio: "1 / 1" }}>
              {previewUrl ? (
                <img src={previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-4)", fontSize: 14, padding: 24, textAlign: "center" }}>
                  {t("up_image_hint")}
                </div>
              )}
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontWeight: 700 }}>{display}</div>
              <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>{prompt.trim() || t("up_prompt_ph")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(tags.length ? tags : [t("up_tags_ph")]).map((tag) => (
                  <span key={tag} className="chip" style={{ height: 30, opacity: tags.length ? 1 : 0.6 }}>{tag}</span>
                ))}
              </div>
            </div>
          </section>

          <section className="card" style={{ padding: 20, borderRadius: "var(--r-xl)", display: "grid", gap: 12 }}>
            <h2 style={{ fontSize: 18 }}>{t("up_recent")}</h2>
            {recentItems.length === 0 ? (
              <p className="muted" style={{ fontSize: 14 }}>{t("up_recent_empty")}</p>
            ) : recentItems.map((item) => (
              <div key={item.id} style={{ display: "grid", gridTemplateColumns: "72px minmax(0,1fr)", gap: 12, alignItems: "center" }}>
                <div style={{ borderRadius: 14, overflow: "hidden", aspectRatio: "1 / 1", background: "var(--surface-2)" }}>
                  <img src={item.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
                <div style={{ minWidth: 0, display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.fileName}</div>
                  <div className="muted" style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.prompt}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {item.tags.map((tag) => (
                      <span key={tag} style={{ height: 24, padding: "0 8px", borderRadius: 999, background: "var(--surface-2)", color: "var(--ink-3)", display: "inline-flex", alignItems: "center", fontSize: 11.5, fontWeight: 700 }}>#{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}

function UploadGate({ onLogin, go }) {
  const { t } = useT();
  return <LoginGate onLogin={onLogin} go={go} title={t("up_gate_title")} desc={t("up_gate_desc")} primaryLabel={t("up_gate_btn")} secondaryLabel={t("gate_browse")} icon="image" primaryIcon="image" />;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error || new Error("file-read-failed"));
    reader.readAsDataURL(file);
  });
}

function UploadField({ label, children }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <label style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-3)" }}>{label}</label>
      {children}
    </div>
  );
}

const uploadInputStyle = {
  width: "100%",
  height: 48,
  padding: "0 14px",
  borderRadius: "var(--r-md)",
  border: "1px solid var(--line-2)",
  background: "var(--paper)",
  color: "var(--ink)",
  fontSize: 14,
  fontFamily: "inherit",
};

const uploadTextareaStyle = {
  ...uploadInputStyle,
  minHeight: 164,
  height: "auto",
  padding: "14px 16px",
  resize: "vertical",
};

const uploadReadOnlyStyle = {
  minHeight: 56,
  padding: "10px 14px",
  borderRadius: "var(--r-md)",
  border: "1px solid var(--line-2)",
  background: "var(--surface-2)",
  display: "flex",
  alignItems: "center",
  gap: 12,
};

Object.assign(window, { Upload, UploadGate });